import { FormField } from './FormField';
import { FormSection } from './FormSection';
import { SupportChecklist } from './SupportChecklist';
import type { ProjectFormValues, SupportItemInput } from '../../schemas/project.schema';
import { Input } from '@/shared/components/ui/input';
import { Select } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import { formatCurrency, parseCurrency } from '@/shared/lib/format';
import type { CatalogOption, SupportTypeOption } from '@/shared/types/domain';

interface SectionSupportProps {
  values: ProjectFormValues;
  supportTypes: SupportTypeOption[];
  fundingSources: CatalogOption[];
  onChangeText: (patch: Partial<ProjectFormValues>) => void;
  onChangeNow: (patch: Partial<ProjectFormValues>) => void;
}

/**
 * Apoio e custos — a única seção inteiramente opcional (§7.1).
 *
 * Ela se anuncia como tal na etiqueta do cabeçalho, e é isso que protege a
 * métrica de abandono (PRD §6.3): o custo nunca foi o número de campos, e sim
 * a pessoa achar que precisa preencher tudo antes de enviar.
 */
export function SectionSupport({
  values,
  supportTypes,
  fundingSources,
  onChangeText,
  onChangeNow,
}: SectionSupportProps) {
  function updateItems(next: SupportItemInput[]) {
    onChangeNow({ support_items: next });
  }

  function handleToggle(typeId: string, checked: boolean) {
    updateItems(
      checked
        ? [...values.support_items, { support_type_id: typeId }]
        : values.support_items.filter((item) => item.support_type_id !== typeId),
    );
  }

  function handleRequest(typeId: string, request: string) {
    onChangeText({
      support_items: values.support_items.map((item) =>
        item.support_type_id === typeId ? { ...item, request: request || null } : item,
      ),
    });
  }

  function handleNeededBy(typeId: string, neededBy: string) {
    updateItems(
      values.support_items.map((item) =>
        item.support_type_id === typeId ? { ...item, needed_by: neededBy || null } : item,
      ),
    );
  }

  return (
    <FormSection
      id="apoio-e-custos"
      number={4}
      title={messages.projectSections.support}
      description={messages.form.supportHint}
      optional
    >
      <SupportChecklist
        supportTypes={supportTypes}
        items={values.support_items}
        onToggle={handleToggle}
        onChangeRequest={handleRequest}
        onChangeNeededBy={handleNeededBy}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Orçamento estimado"
          hint={
            values.estimated_budget_cents !== null
              ? `${formatCurrency(values.estimated_budget_cents)} — ${messages.form.budgetHint}`
              : messages.form.budgetHint
          }
        >
          <Input
            inputMode="decimal"
            placeholder="Ex.: 1.200,00"
            defaultValue={
              values.estimated_budget_cents !== null
                ? (values.estimated_budget_cents / 100).toFixed(2).replace('.', ',')
                : ''
            }
            onChange={(event) =>
              onChangeText({ estimated_budget_cents: parseCurrency(event.target.value) })
            }
          />
        </FormField>

        <FormField label="De onde vem o recurso">
          <Select
            value={values.funding_source_id ?? ''}
            onChange={(event) =>
              onChangeNow({ funding_source_id: event.target.value || null })
            }
          >
            <option value="">Selecione…</option>
            {fundingSources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      <FormField label="Observações sobre o orçamento">
        <Textarea
          rows={3}
          maxLength={1000}
          value={values.budget_notes ?? ''}
          placeholder="Ex.: valor cobre material das oficinas; o transporte é por conta dos participantes."
          onChange={(event) => onChangeText({ budget_notes: event.target.value || null })}
        />
      </FormField>
    </FormSection>
  );
}
